'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import {
  normalizeSiteLanguage,
  SITE_LANGUAGE_COOKIE,
  SITE_LANGUAGE_STORAGE_KEY,
  type SiteLanguage,
} from '@/lib/site-language-config';

interface SiteLanguageContextValue {
  language: SiteLanguage;
  setLanguage: (language: SiteLanguage) => void;
}

const SiteLanguageContext = createContext<SiteLanguageContextValue | undefined>(undefined);

export function SiteLanguageProvider({
  children,
  initialLanguage,
}: {
  children: ReactNode;
  initialLanguage: SiteLanguage;
}) {
  const router = useRouter();
  const [language, setLanguageState] = useState<SiteLanguage>(initialLanguage);

  useEffect(() => {
    const savedLanguage = normalizeSiteLanguage(
      typeof window !== 'undefined'
        ? window.localStorage.getItem(SITE_LANGUAGE_STORAGE_KEY)
        : initialLanguage
    );

    if (savedLanguage !== language) {
      setLanguageState(savedLanguage);
    }
  }, [initialLanguage]);

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem(SITE_LANGUAGE_STORAGE_KEY, language);
    document.cookie = `${SITE_LANGUAGE_COOKIE}=${language}; path=/; max-age=31536000; samesite=lax`;
  }, [language]);

  const value = useMemo<SiteLanguageContextValue>(
    () => ({
      language,
      setLanguage: (nextLanguage) => {
        if (nextLanguage === language) {
          return;
        }

        setLanguageState(nextLanguage);
        router.refresh();
      },
    }),
    [language, router]
  );

  return <SiteLanguageContext.Provider value={value}>{children}</SiteLanguageContext.Provider>;
}

export function useSiteLanguage() {
  const context = useContext(SiteLanguageContext);

  if (!context) {
    throw new Error('useSiteLanguage must be used within a SiteLanguageProvider');
  }

  return context;
}
